import type { ReactElement, ForwardedRef } from "react";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { ButtonSpinner } from "./ButtonSpinner";

export type ButtonProps = {
  color?:
    | "primary"
    | "primary-dark"
    | "slim"
    | "secondary-dark"
    | "secondary"
    | "tertiary"
    | "inverse-red"
    | "transparent"
    | "red"
    | "green"
    | "purple";
  size?: "small" | "large";
  loading?: boolean;
  selected?: boolean;
};

export const Button = forwardRef(
  (
    props: React.ButtonHTMLAttributes<HTMLButtonElement> & ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ): ReactElement => {
    //size needs to be in this button as otherwise we run into CSS specificity issues

    const { loading, ...buttonProps } = props;

    const colour = props?.color ?? "primary";

    return (
      <button
        type="button"
        {...buttonProps}
        disabled={props.disabled || loading}
        ref={ref}
        className={twMerge(
          props.size === "small" ? "w-fit p-2 text-xs" : "w-fit p-4 text-sm",

          (() => {
            switch (colour) {
              case "primary":
                return "bg-zinc-800 text-white hover:bg-zinc-700 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100";
              case "primary-dark":
                return "bg-white text-zinc-800 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-white dark:hover:bg-zinc-700";
              case "secondary-dark":
                return "border border-white bg-transparent text-white hover:bg-white/10 dark:border-zinc-700 dark:hover:bg-zinc-800/50";
              case "secondary":
                return "border border-zinc-300 bg-zinc-100 text-zinc-800 hover:bg-zinc-200 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800/80";
              case "slim":
                return "border border-zinc-300 bg-white px-3 py-2 text-sm font-medium leading-[18px] hover:bg-zinc-100 hover:stroke-zinc-300 hover:text-zinc-800 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-white";
              case "inverse-red":
                return "text-red-700 hover:bg-gray-200 dark:text-red-500 dark:hover:bg-red-950/20";
              case "transparent":
              case "tertiary":
                return "bg-transparent text-black hover:bg-gray-100 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-white";
              case "red":
                return "bg-red-600 text-white hover:bg-red-700";
              case "green":
                return "bg-green-600 text-white hover:bg-green-700";
              case "purple":
                return "bg-[#B557F1] text-white hover:bg-[#9245c2]";
            }
          })(),

          props.disabled || props.loading
            ? "cursor-not-allowed opacity-50"
            : "cursor-pointer",

          props.loading && "text-transparent [&>*:not(:first-child)]:invisible",

          "relative flex items-center justify-center gap-2 rounded-lg text-center leading-5 font-semibold tracking-wide whitespace-nowrap not-italic transition-colors first-of-type:ml-0 last-of-type:mr-0 motion-reduce:transition-none",

          props.color !== "slim" && "uppercase",

          !colour?.includes("secondary") && colour !== "slim" && "border-none",

          colour === "slim" && "rounded-md",
          colour === "slim" &&
            props.selected &&
            "border-zinc-500 bg-zinc-200 hover:bg-zinc-300 hover:stroke-zinc-400 hover:text-zinc-800 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white",
          props.className,
        )}
      >
        {props.loading && (
          <div className={"absolute"}>
            <ButtonSpinner
              className={
                props.color === "secondary"
                  ? "text-black dark:text-white"
                  : "text-white"
              }
            />
          </div>
        )}
        {props.children}
      </button>
    );
  },
);

export const SmallButton = (
  props: React.ButtonHTMLAttributes<HTMLButtonElement> &
    Omit<ButtonProps, "size">,
): ReactElement => {
  return <Button {...props} size={"small"} />;
};

Button.displayName = "Button";
